
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, Cell } from "recharts";

interface MetricsCardProps {
  title: string;
  value: string;
  period: string;
  weekValue: string;
  weekPeriod: string;
  chartData: number[];
  chartColor: string;
  icon: React.ReactNode;
  maxValue: number;
  labels: string[];
  chartType: "line" | "donut";
  isLoading?: boolean;
}

export function MetricsCard({
  title,
  value,
  period,
  weekValue,
  weekPeriod,
  chartData,
  chartColor,
  icon,
  maxValue,
  labels,
  chartType,
  isLoading = false
}: MetricsCardProps) {
  const donutChartData = chartData.map((value, idx) => ({
    name: labels[idx],
    value: value,
  }));

  const lineChartData = chartData.map((value, idx) => ({
    name: labels[idx],
    value: value,
  }));

  const renderDonutChart = () => (
    <ResponsiveContainer width="100%" height={180}>
      <PieChart>
        <Pie
          data={donutChartData}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={80}
          paddingAngle={5}
          dataKey="value"
          stroke="none"
        >
          {donutChartData.map((_, index) => (
            <Cell key={`cell-${index}`} fill={chartColor} />
          ))}
        </Pie>
        <Tooltip 
          formatter={(value) => [value, title]}
          itemStyle={{ color: "#fff" }} 
          contentStyle={{ 
            backgroundColor: "#1e1e1e", 
            border: "none",
            borderRadius: "4px"
          }} 
        />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderLineChart = () => (
    <ResponsiveContainer width="100%" height={180}>
      <LineChart data={lineChartData}>
        <Line
          type="monotone"
          dataKey="value"
          stroke={chartColor}
          strokeWidth={2}
          dot={false}
        />
        <Tooltip
          formatter={(value) => [value, title]}
          labelFormatter={(label) => label}
          itemStyle={{ color: "#fff" }}
          contentStyle={{
            backgroundColor: "#1e1e1e",
            border: "none",
            borderRadius: "4px"
          }}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  return (
    <Card className="bg-[#0c0c0c] border border-gray-800/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-gray-400 flex items-center">
          {icon}
          <span className="ml-2">{title}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div>
            {isLoading ? (
              <Skeleton className="h-9 w-24 bg-gray-800" />
            ) : (
              <div className="text-3xl font-bold">{value}</div>
            )}
            <p className="text-xs text-gray-400">{period}</p>
          </div>
          <div>
            {isLoading ? (
              <Skeleton className="h-6 w-28 bg-gray-800" />
            ) : (
              <div className="text-xl font-medium">{weekValue}</div>
            )}
            <p className="text-xs text-gray-400">{weekPeriod}</p>
          </div>
          
          {isLoading ? (
            <Skeleton className="h-[180px] w-full bg-gray-800" />
          ) : (
            chartType === "donut" ? renderDonutChart() : renderLineChart()
          )}
          
          <div className="flex justify-between items-center text-xs text-gray-500 px-2">
            {labels.map((label, idx) => (
              <div key={idx}>{label}</div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
